import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { Dropdown, option } from '@monorepo/base/src/components/dropdown/dropdown';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Card } from '@monorepo/base/src/components/card/card';
import { IAdminxStore } from '../../../../stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { filterDestinationsByAdvertiser, resolveDestinationInternalPricing } from '../../../../utils/campaign-destination';
import {
	findWebappsCidByOptionLabel,
	findWebappsCidLabel,
	formatWebappsCidLabel,
	parseCidFromWebappsCidOptionLabel,
	toWebappsCidDropdownOptionLabels,
} from '../../../../utils/webapps-cid-options';
import { hasLinkedWebappsCidSegments } from '../../../../utils/webapps-cid-segments';
import { useCampaignWebappsCidOptions } from '../../../../hooks/apis/use-campaign-webapps-cid-options';
import { useCampaignPayouts } from '../../../../hooks/apis/use-campaign-payouts';
import { useDestinationCreatives } from '../../../../hooks/apis/use-destination-creatives';
import { WebappsCidModel } from '../../../../models/webapps-cid.model';
import { CampaignDestinationLinkModel, IEntity } from '../../../../models/campaign.model';
import { Statuses } from '../../../../enums/status';
import { Decisions } from '../../../../enums/decisions';
import { Tabs } from '@monorepo/base/src/components/tabs/tabs';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import { Flex } from '@monorepo/base/src/components/flex/flex';
import { ActionModal, ModalTypes } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import { hasPausedDestination } from '../../../../utils/campaign-destination-weights';
import styles from '../campaign-form.module.scss';
import { FormError } from '@monorepo/tools/src/lib/models/form-error.model';
import { FormError as FormErrorMessage } from '@monorepo/base/src/components/form/form-error/form-error';
import { DestinationGuardModal } from '../../../../components/modals/destination-guard-modal';
import { DestinationsWeightTable } from './destinations-weight-table';
const DestinationItem = observer(({ link }: { link: CampaignDestinationLinkModel }) => {
	const { campaignStore, modalsStore } = useStores<IAdminxStore>();
	const campaignCrudStore = campaignStore.getCrud();
	const formStore = campaignCrudStore.getFormStore();
	const campaign = campaignCrudStore.getData();
	const inputOptions = campaignStore.getInputOptions();
	const accounts = inputOptions.getAccounts();
	const [webappsCidDropdownKey, setWebappsCidDropdownKey] = useState(0);

	const destinations = campaignStore.getDestinations().getDestinations();
	const advertiserId = campaign.getAdvertiser()?.getId() ?? link.destination_dto?.advertiser?.id;
	const advertiserDestinations = useMemo(() => filterDestinationsByAdvertiser(destinations, advertiserId), [destinations, advertiserId]);

	const selectedDestination = advertiserDestinations.find(
		destination => String(destination.getId()) === String(link.getSelectedDestinationId())
	);
	const useInternalPricing = link.getDestinationUseInternalPricing() || resolveDestinationInternalPricing(selectedDestination, accounts);

	const { cids: webappsCids, isLoading: webappsCidsLoading } = useCampaignWebappsCidOptions(useInternalPricing);

	const webappsCidOptions = useMemo(() => {
		const labels = toWebappsCidDropdownOptionLabels(webappsCids);
		const hydratedLabel = link.hydratedWebappsCidLabel;
		const selectedCid = link.getSelectedWebappsCid();
		if (hydratedLabel && selectedCid && !labels.includes(hydratedLabel)) {
			return [hydratedLabel, ...labels];
		}
		return labels;
	}, [webappsCids, link.hydratedWebappsCidLabel, link.getSelectedWebappsCid()]);

	const selectedWebappsCidOption = useMemo(
		() => findWebappsCidLabel(webappsCids, link.getSelectedWebappsCid(), link.hydratedWebappsCidLabel),
		[webappsCids, link.getSelectedWebappsCid(), link.hydratedWebappsCidLabel]
	);

	const targetingGeos = campaign.getTargeting()?.getGeos() ?? [];
	const destinationId = link.getSelectedDestinationId();
	const webappsCidId = link.getSelectedWebappsCidDto()?.id;
	const { payoutsByGeo, canFetch: canFetchPayout } = useCampaignPayouts({
		destinationId,
		geos: targetingGeos,
		webappsCidId,
		webappsCidRequired: Boolean(useInternalPricing),
	});
	const { creatives: destinationCreatives, isLoading: isLoadingCreatives } = useDestinationCreatives(destinationId, advertiserId);

	const creativeOptions = useMemo(
		() =>
			destinationCreatives
				.map(creativeLink => {
					const creative = creativeLink.getCreative();
					if (!creative?.getId()) {
						return null;
					}
					return `${creative.getBrand()} - ${creative.getId()}`;
				})
				.filter((label): label is string => Boolean(label)),
		[destinationCreatives]
	);

	const payoutPlaceholder = useMemo(() => {
		if (!destinationId) {
			return 'Select a destination to see payout';
		}
		if (targetingGeos.length === 0) {
			return 'Set countries in Targeting to see payout';
		}
		if (useInternalPricing && !webappsCidId) {
			return 'Select Web Apps CID to see payout';
		}
		return '';
	}, [destinationId, targetingGeos.length, useInternalPricing, webappsCidId]);

	const handleActiveChange = (active: boolean) => {
		if (!active && campaign.getStatus() === Statuses.Active) {
			const otherActiveDestinations = campaign
				.getCampaignDestinations()
				.some(d => String(d.getCatalogDestinationId()) !== String(link.getCatalogDestinationId()) && d.isActive());
			if (!otherActiveDestinations) {
				const msg = 'You must have at least one active destination';
				formStore.addError(new FormError('campaign_destinations', msg));

				modalsStore.addModal({
					onDismiss: () => formStore.clearError('campaign_destinations'),
					children: <DestinationGuardModal message={msg} onClose={() => formStore.clearError('campaign_destinations')} />,
				});
				return;
			}
		}
		link.setStatus(active ? Statuses.Active : Statuses.Paused);
		formStore.clearError('campaign_destinations');
	};

	const formatPayoutValue = (geo: string): string => {
		const payout = payoutsByGeo[geo];
		if (!payout || payout.isLoading || payout.error || payout.value == null) {
			return '';
		}
		return String(payout.value);
	};

	const applyWebappsCid = (newCid: id | string | undefined, optionLabel?: string, match?: WebappsCidModel) => {
		if (newCid == null || newCid === '') {
			link.setSelectedWebappsCid(undefined);
			link.setSelectedWebappsCidDto(undefined);
			link.hydratedWebappsCidLabel = undefined;
		} else {
			const resolvedMatch = match ?? webappsCids.find(item => String(item.getCid()) === String(newCid));
			const previousCid = link.getSelectedWebappsCid();
			const isChanging = previousCid != null && String(previousCid) !== String(newCid);
			const previousDto = link.getSelectedWebappsCidDto();

			link.setSelectedWebappsCid(newCid);
			link.setSelectedWebappsCidDto({
				id: isChanging ? resolvedMatch?.getId() : (resolvedMatch?.getId() ?? previousDto?.id),
				cid: newCid,
				product_id: resolvedMatch?.product_id ?? previousDto?.product_id,
				product_name: resolvedMatch?.getProductName() ?? previousDto?.product_name,
			});
			link.hydratedWebappsCidLabel = resolvedMatch
				? formatWebappsCidLabel(resolvedMatch.getCid(), resolvedMatch.getName())
				: optionLabel || formatWebappsCidLabel(newCid);
		}
		link.recomputeDestinationUrl();
		campaign.syncPrimaryDestinationUrl();
		formStore.clearError('webapps_cid');
		formStore.clearError('campaign_destinations');
	};

	const onWebappsCidSelect = async (optionLabel: option | undefined) => {
		if (!optionLabel) {
			applyWebappsCid(undefined);
			return;
		}

		const match = findWebappsCidByOptionLabel(webappsCids, optionLabel);
		const newCid = match?.getCid() ?? parseCidFromWebappsCidOptionLabel(optionLabel);
		if (newCid == null || newCid === '') {
			return;
		}

		const previousCid = link.getSelectedWebappsCid();
		const isEdit = Boolean(campaign.getId());
		const isChanging = previousCid != null && String(previousCid) !== String(newCid);

		const confirmAndApply = () => {
			applyWebappsCid(newCid, optionLabel, match);
		};

		if (!isEdit || !isChanging) {
			confirmAndApply();
			return;
		}
		const hasSegments = await hasLinkedWebappsCidSegments(previousCid);
		if (!hasSegments) {
			confirmAndApply();
			return;
		}

		modalsStore.addModal({
			onDismiss: () => setWebappsCidDropdownKey(key => key + 1),
			children: (
				<ActionModal
					type={ModalTypes.Warning}
					primaryBtnTitle={'Change CID'}
					primaryBtnClassName={styles.webappsCidChangeModalBtn}
					onAction={confirmAndApply}>
					<Fragment>
						<h3>This Web Apps CID has linked segmentations</h3>
						<p>
							This campaign&apos;s Web Apps CID is mapped to one or more segmentation groups. Changing it here updates this
							campaign&apos;s destination URL only.
						</p>
					</Fragment>
				</ActionModal>
			),
		});
	};

	const [weightDraft, setWeightDraft] = useState(String(link.weight ?? 1));
	const [maxDailySalesDraft, setMaxDailySalesDraft] = useState(link.daily_max_sales == null ? '' : String(link.daily_max_sales));
	const [maxDailyLossDraft, setMaxDailyLossDraft] = useState(link.daily_max_loss == null ? '' : String(link.daily_max_loss));

	useEffect(() => {
		setWeightDraft(String(link.weight ?? 1));
		setMaxDailySalesDraft(link.daily_max_sales == null ? '' : String(link.daily_max_sales));
		setMaxDailyLossDraft(link.daily_max_loss == null ? '' : String(link.daily_max_loss));
	}, [link.weight, link.daily_max_sales, link.daily_max_loss, link.getCatalogDestinationId()]);

	const applyWeight = (value: number) => {
		link.setWeight(value);
		setWeightDraft(String(value));
		formStore.clearError('campaign_destinations');
	};

	const commitWeight = () => {
		const value = weightDraft === '' ? 1 : parseFloat(weightDraft);
		if (value <= 0) {
			formStore.addError(new FormError('campaign_destinations', 'Weight must be greater than 0'));
			setWeightDraft(String(link.weight ?? 1));
			return;
		}
		const nextWeight = Number.isNaN(value) ? 1 : value;
		if (nextWeight === (link.weight ?? 1)) {
			setWeightDraft(String(link.weight ?? 1));
			return;
		}

		if (hasPausedDestination(campaign.getCampaignDestinations())) {
			modalsStore.addModal({
				onDismiss: () => setWeightDraft(String(link.weight ?? 1)),
				children: (
					<ActionModal
						type={ModalTypes.Warning}
						primaryBtnTitle="Continue"
						onAction={() => applyWeight(nextWeight)}
						onClose={() => setWeightDraft(String(link.weight ?? 1))}>
						<Fragment>
							<h3>Paused destinations are excluded from traffic</h3>
							<p>
								This campaign has at least one paused destination. Traffic percentages are calculated only from active
								destinations. Do you want to continue updating this weight?
							</p>
						</Fragment>
					</ActionModal>
				),
			});
			return;
		}

		applyWeight(nextWeight);
	};

	const commitNullableNumber = (
		draft: string,
		current: number | null | undefined,
		setDraft: (value: string) => void,
		setValue: (value: number | null) => void
	) => {
		const nextValue = draft === '' ? null : parseFloat(draft);
		if (nextValue != null && Number.isNaN(nextValue)) {
			setDraft(current == null ? '' : String(current));
			return;
		}
		if (nextValue === (current ?? null)) {
			setDraft(current == null ? '' : String(current));
			return;
		}
		setValue(nextValue);
		setDraft(nextValue == null ? '' : String(nextValue));
		formStore.clearError('campaign_destinations');
	};

	return (
		<Fragment>
			<Flex justifyContent="flex-start" gap="24px" alignItems="center">
				<Toggle
					title="Destination active?"
					label={link.isActive() ? Decisions.Yes : Decisions.No}
					onChange={active => {
						handleActiveChange(active);
					}}
					defaultValue={link.isActive()}
					debugProps={{ dataAttrs: [new DataAttribute('id', `destination_active_${link.getCatalogDestinationId()}`)] }}
				/>
			</Flex>

			<div className={styles.destinationRow}>
				<Input
					topLabel="Destination"
					value={link.getName()}
					disabled={true}
					debugProps={{ dataAttrs: [new DataAttribute('id', `destination_name_${link.getCatalogDestinationId()}`)] }}
				/>
				{useInternalPricing ? (
					<Dropdown
						key={webappsCidDropdownKey}
						required={true}
						label={'Web Apps CID'}
						autocomplete={true}
						autocompletePlaceholder={'Search Web Apps CID'}
						isLoading={webappsCidsLoading}
						defaultOption={selectedWebappsCidOption}
						options={webappsCidOptions}
						onSelect={(val: Set<option> | undefined) => onWebappsCidSelect(val?.values().next().value)}
						error={formStore.getErrors().get('webapps_cid')?.getMsg()}
						debugProps={{ dataAttrs: [new DataAttribute('id', `webapps_cid_${link.getCatalogDestinationId()}`)] }}
						isKeyboardNavigation={true}
					/>
				) : null}
			</div>
			<Input
				className={styles.additionalUrlParams}
				topLabel={'Additional URL params'}
				placeholder={'e.g. foo=bar&baz=1'}
				value={link.getAdditionalUrlParams()}
				onChange={e => {
					link.setAdditionalUrlParams(e.target.value);
					link.recomputeDestinationUrl();
					campaign.syncPrimaryDestinationUrl();
				}}
				disabled={!link.getSelectedDestinationId()}
				debugProps={{ dataAttrs: [new DataAttribute('id', `additional_url_params_${link.getCatalogDestinationId()}`)] }}
			/>
			<Flex flexDirection="column" justifyContent="flex-start" gap="24px" alignItems="start">
				<Input
					topLabel="Traffic weight"
					value={weightDraft}
					onValue={value => {
						if (!Number.isNaN(Number(value))) {
							setWeightDraft(value);
						} else {
							setWeightDraft('');
						}
					}}
					onBlur={commitWeight}
					debugProps={{ dataAttrs: [new DataAttribute('id', `destination_weight_${link.getCatalogDestinationId()}`)] }}
				/>
				<Input
					topLabel="Max daily sales"
					value={maxDailySalesDraft}
					onValue={value => {
						if (value === '' || !Number.isNaN(Number(value))) {
							setMaxDailySalesDraft(value);
						}
					}}
					onBlur={() =>
						commitNullableNumber(maxDailySalesDraft, link.daily_max_sales, setMaxDailySalesDraft, value =>
							link.setDailyMaxSales(value)
						)
					}
					debugProps={{ dataAttrs: [new DataAttribute('id', `destination_daily_max_sales_${link.getCatalogDestinationId()}`)] }}
				/>
				<Input
					topLabel="Max daily loss"
					value={maxDailyLossDraft}
					onValue={value => {
						if (value === '' || !Number.isNaN(Number(value))) {
							setMaxDailyLossDraft(value);
						}
					}}
					onBlur={() =>
						commitNullableNumber(maxDailyLossDraft, link.daily_max_loss, setMaxDailyLossDraft, value =>
							link.setDailyMaxLoss(value)
						)
					}
					debugProps={{ dataAttrs: [new DataAttribute('id', `destination_daily_max_loss_${link.getCatalogDestinationId()}`)] }}
				/>
			</Flex>
			<Spacer />
			{canFetchPayout ? (
				targetingGeos.map(geo => (
					<Input
						key={geo}
						className={styles.payoutField}
						topLabel={targetingGeos.length > 1 ? `Payout (${geo})` : 'Payout'}
						placeholder={payoutsByGeo[geo]?.isLoading ? 'Loading...' : ''}
						value={formatPayoutValue(geo)}
						disabled={true}
						debugProps={{ dataAttrs: [new DataAttribute('id', `payout_${geo}_${link.getCatalogDestinationId()}`)] }}
					/>
				))
			) : (
				<Input
					className={styles.payoutField}
					topLabel={'Payout'}
					placeholder={payoutPlaceholder}
					value={''}
					disabled={true}
					debugProps={{ dataAttrs: [new DataAttribute('id', `payout_${link.getCatalogDestinationId()}`)] }}
				/>
			)}
			<Dropdown
				key={String(destinationId ?? 'none')}
				label={'Creatives'}
				options={creativeOptions}
				defaultOptions={new Set(creativeOptions)}
				multi={true}
				disabled={true}
				wrapTags={true}
				isLoading={isLoadingCreatives}
				placeholder={destinationId ? 'No creatives linked' : 'Select a destination to see creatives'}
				debugProps={{ dataAttrs: [new DataAttribute('id', `campaign_destination_creatives_${link.getCatalogDestinationId()}`)] }}
			/>
		</Fragment>
	);
});

export const Destination: FC = observer(() => {
	const { campaignStore } = useStores<IAdminxStore>();
	const campaignCrudStore = campaignStore.getCrud();
	const formStore = campaignCrudStore.getFormStore();
	const campaign = campaignCrudStore.getData();
	const inputOptions = campaignStore.getInputOptions();
	const accounts = inputOptions.getAccounts();

	const destinations = campaignStore.getDestinations().getDestinations();
	const destinationsLoading = campaignStore.getDestinations().getIsLoading();
	const advertiserId =
		campaign.getAdvertiser()?.getId() ??
		campaign.getCampaignDestinations()[0]?.destination_dto?.advertiser?.id ??
		campaign.campaign_destination?.destination_dto?.advertiser?.id;

	const advertiserDestinations = useMemo(() => filterDestinationsByAdvertiser(destinations, advertiserId), [destinations, advertiserId]);
	const availableDestinations = campaign.getAvailableDestinations(advertiserDestinations);

	const [currentDestinationId, setCurrentDestinationId] = useState<id | undefined>();
	const links = campaign.getCampaignDestinations();
	const firstDestinationId = links[0]?.getCatalogDestinationId();
	const currentLink = campaign.getCampaignDestinationByCatalogId(currentDestinationId);

	useEffect(() => {
		if (currentDestinationId !== firstDestinationId && firstDestinationId && !currentDestinationId) {
			setCurrentDestinationId(firstDestinationId);
		}
	}, [currentDestinationId, firstDestinationId]);

	useEffect(() => {
		if (!links.some(link => String(link.getCatalogDestinationId()) === String(currentDestinationId))) {
			setCurrentDestinationId(firstDestinationId);
		}
	}, [links.length, currentDestinationId, firstDestinationId]);

	const addOptions: IEntity[] = availableDestinations
		.map(destination => ({
			id: destination.getId(),
			name: destination.getName() || destination.getDestUrl() || String(destination.getId()),
		}))
		.filter((item): item is IEntity & { id: id } => item.id != null);

	const handleAddTab = (optionEntity: IEntity) => {
		const destination = advertiserDestinations.find(item => String(item.getId()) === String(optionEntity.id));
		if (!destination) {
			return;
		}
		const link = campaign.addCampaignDestination(destination, accounts);
		setCurrentDestinationId(link.getCatalogDestinationId());
		formStore.clearError('campaign_destinations');
	};

	const isDeletableDestination = (linksLength: number) => {
		if (linksLength === 1) {
			return false;
		}
		return true;
	};

	return (
		<Card
			id={'destinations'}
			title={'Destinations'}
			icon={'link-03'}
			debugProps={{ dataAttrs: [new DataAttribute('id', 'destinations_section')] }}>
			<Spacer y={2} />

			<Tabs
				maxVisibleTabs={2}
				addOptions={addOptions}
				onTabChange={setCurrentDestinationId}
				addTabPlaceholder="Please add a destination"
				onAddTab={handleAddTab}
				onRemoveTab={catalogId => {
					campaign.removeCampaignDestination(catalogId);
					formStore.clearError('campaign_destinations');
				}}
				activeTabId={currentDestinationId}
				showActive
				isPhases={false}
				showAddTab={!!advertiserId && !destinationsLoading && addOptions.length > 0}
				tabs={links.map(link => ({
					id: link.getCatalogDestinationId(),
					name: link.getName(),
					active: link.isActive(),
					deletable: isDeletableDestination(links.length),
				}))}
			/>

			<Spacer y={2} />

			{currentLink ? <DestinationItem key={String(currentLink.getCatalogDestinationId())} link={currentLink} /> : null}

			<Spacer y={2} />
			{!!formStore.getErrors().get('campaign_destinations')?.getMsg() && (
				<Fragment>
					<FormErrorMessage msg={formStore.getErrors().get('campaign_destinations')?.getMsg()} />
					<Spacer y={1} />
				</Fragment>
			)}
			<Spacer y={2} />

			<DestinationsWeightTable links={links} showPausedDestinations={true} enableWeightAllocation />
		</Card>
	);
});
